/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Construct from 'can/construct/construct';
import can from 'can';
import _ from 'lodash';

function merge(targetMap, src) {
    if (src instanceof can.Map || src instanceof can.List) {
        src.each((value, key) => {
            mergeValue(targetMap, value, key);
        });
    } else {
        _.each(src, (value, key) => {
            mergeValue(targetMap, value, key);
        });
    }
}

function mergeValue(targetMap, value, key) {
    var targetValue = targetMap[key];
    var targetPropType = typeof targetValue;
    var srcPropType = typeof value;

    if (targetPropType === 'object' && srcPropType === 'object') {

        // can.Map with array or can.List

        if ( (targetValue instanceof can.Map && _.isArrayLike(targetValue)) || targetValue instanceof can.List) {
            // if source is an array (or array-like - Map, List) and the length is the same - merge
            if ((_.isArray(value) || _.isArrayLike(value)) && targetValue.length === value.length) {
                merge(targetValue, value);
            // else set new value (array, object, Map or List)
            } else {
                targetMap.attr(key, value);
            }

        // can.Map

        } else if (targetValue instanceof can.Map) {
            // merge
            merge(targetValue, value);

        // array

        } else if (_.isArray(targetValue)) {
            // replace with new value (array or object)
            targetMap.attr(key, value);

        // object

        } else {
            targetMap.attr(key, _.extend(targetValue, value));
        }

    } else {
        targetMap.attr(key, value);
    }
}

export default Construct.extend({

    debouncedGet: function () {
        var args = Array.prototype.slice.call(arguments, 0, arguments.length);
        var dependencies = args.slice(0, args.length - 2);
        var fn = args.slice(args.length - 2, args.length - 1)[0];
        var delay = args.slice(args.length - 1, args.length)[0];
        var contextName = 'debouncedGetContext-' + _.uniqueId();

        return function (lastSetValue, setAttrValue) {
            // binding to dependencies
            _.each(dependencies, name => this.attr(name));
            var context = (this[contextName] = this[contextName] || {});

            if (!context.debounced) {
                context.debounced = can.debounce(function () {
                    var [self, lastValue, settter] = context.lastArguments;
                    var value = fn.apply(self, _.map(dependencies, name => self.attr(name)));
                    if (value !== lastValue) {
                        settter(value);
                    }
                }, delay);
            }
            context.lastArguments = [this, lastSetValue, setAttrValue];
            context.debounced();
        };
    },

    throttleMethod: function (fn, timeout) {
        var name = 'throttleMethod-' + _.uniqueId();
        return function () {
            var throttled = (this[name] = this[name] || _.throttle(fn, timeout));
            throttled.apply(this, arguments);
        };
    },

    merge: function (targetMap, src) {
        return merge(targetMap, src);
    }

}, {});
